import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { FaEye, FaPlus, FaUserPlus } from 'react-icons/fa';
import Table from '../../components/Table';
import Loader from '../../components/Loader';
import Button from '../../components/Button';
import InputSeason from '../../components/InputSeason/InputSeason';
import translations from '../../translations/de';
import Paths from '../../constants/paths';
import parkingAPI from '../../api/parking';
import formatPin from '../../helpers/formatPin';
import AssignPassToFanModal from './AssignPassToFanModal';
import AssignOwnerModal from '../ParkingFields/AssignOwnerModal';

// Display name of the app user a pass is assigned to (owner), if any.
const ownerName = (p) =>
    p.user ? [p.user.first_name, p.user.last_name].filter(Boolean).join(' ').trim() || p.user.email || `#${p.user.id}` : '';

// List of a fan's parking passes (Parkkarten), mirroring the Abos tab, with the
// option to assign a free pass to this fan (owner) or re-assign/remove per pass.
const ParkingTickets = ({ fanID }) => {
    const history = useHistory();
    const [loader, setLoader] = useState(false);
    const [passes, setPasses] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [reload, setReload] = useState(0);
    const [showAssign, setShowAssign] = useState(false);
    // Existing pass whose owner is being re-assigned/removed (opens the modal).
    const [ownerModalPass, setOwnerModalPass] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoader(true);
            try {
                const res = await parkingAPI.getPasses({
                    params: { user_id: fanID, season_id: selectedSeason },
                });
                setPasses(res.data || []);
            } catch (e) {
                console.log(e);
                setPasses([]);
            }
            setLoader(false);
        };
        selectedSeason && load();
    }, [fanID, selectedSeason, reload]);

    const columns = [
        { Header: 'ID', accessor: 'id', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.parking_field,
            accessor: 'field',
            Cell: ({ value }) => <div>{value}</div>,
        },
        { Header: translations.type, accessor: 'type', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.parking_code,
            accessor: 'code',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: '',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="button-cell">
                    <Button
                        title="Zuteilen / Ändern"
                        onClick={() =>
                            setOwnerModalPass(passes.find((p) => p.id === row.original.id))
                        }
                    >
                        <FaUserPlus />
                    </Button>
                    <Button
                        title={translations.show}
                        onClick={() =>
                            history.push(Paths.PARKING_PASS.replace(':pass_id', row.original.id))
                        }
                    >
                        <FaEye />
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="tab-controls">
                <div style={{ display: 'block' }}>
                    <InputSeason
                        selectedSeason={selectedSeason}
                        setSelectedSeason={setSelectedSeason}
                    />
                </div>
                {/* Hidden label spacer aligns the button top with the season select
                    (which sits below its SPIELZEIT label). */}
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '20px' }}>
                    <label
                        style={{
                            fontWeight: 'bold',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            visibility: 'hidden',
                        }}
                    >
                        &nbsp;
                    </label>
                    <Button label="Parkkarte zuteilen" onClick={() => setShowAssign(true)}>
                        <FaPlus style={{ marginRight: '5px' }} />
                    </Button>
                </div>
            </div>

            {loader ? (
                <div className="loader-wrapper">
                    <Loader size="big" theme="dark" />
                </div>
            ) : passes.length !== 0 ? (
                <Table
                    isRowClickable={false}
                    columns={columns}
                    data={passes.map((p) => ({
                        id: p.id,
                        field: p.parking_field?.name ?? '-',
                        type: p.type === 'full_season' ? translations.full_season : 'Einzelspiel',
                        code: formatPin(p.assign_token),
                    }))}
                />
            ) : (
                <p className="no-itmes">No data yet!</p>
            )}

            {showAssign && (
                <AssignPassToFanModal
                    fanId={fanID}
                    seasonId={selectedSeason}
                    onClose={() => setShowAssign(false)}
                    onAssigned={() => {
                        setShowAssign(false);
                        setReload((r) => r + 1);
                    }}
                />
            )}

            {ownerModalPass && (
                <AssignOwnerModal
                    passId={ownerModalPass.id}
                    currentOwner={ownerName(ownerModalPass)}
                    onClose={() => setOwnerModalPass(null)}
                    onAssigned={() => {
                        setOwnerModalPass(null);
                        setReload((r) => r + 1);
                    }}
                />
            )}
        </div>
    );
};

export default observer(ParkingTickets);
